import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const currentYear = new Date().getFullYear();

  const navigationLinks = [
    { label: 'Home', href: '/' },
    { label: 'Revenue Growth Engine', href: '/revenue-growth-engine' },
    { label: 'Marketing Automation', href: '/marketing-automation' },
    { label: 'Customer Support AI', href: '/customer-support-ai' },
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Contact', href: '/contact' },
  ];

  const socialLinks = [
    { name: 'LinkedIn', icon: 'LinkIcon', href: '#' },
    { name: 'Twitter', icon: 'LinkIcon', href: '#' },
    { name: 'Facebook', icon: 'LinkIcon', href: '#' },
  ];

  return (
    <footer className={`bg-secondary text-secondary-foreground ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Image
                src="/assets/images/rlx-stamp-whitebg-1768239564312.png"
                alt="RedLetterX AI Logo"
                width={40}
                height={40}
                className="object-contain"
              />
              <span className="text-xl font-heading font-bold">RedLetterX AI</span>
            </div>
            <p className="text-sm text-secondary-foreground/80 leading-relaxed">
              Strategic AI consulting that transforms business operations through intelligent automation and data-driven insights.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base font-heading font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {navigationLinks.slice(0, 3).map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-secondary-foreground/80 hover:text-accent transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-base font-heading font-semibold mb-4">Services</h3>
            <ul className="space-y-2">
              {navigationLinks.slice(3, 6).map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-secondary-foreground/80 hover:text-accent transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-base font-heading font-semibold mb-4">Get In Touch</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Icon name="EnvelopeIcon" size={20} className="text-accent mt-0.5" />
                <a
                  href="mailto:hello@redletterx.ai"
                  className="text-sm text-secondary-foreground/80 hover:text-accent transition-colors duration-300"
                >
                  hello@redletterx.ai
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <Icon name="PhoneIcon" size={20} className="text-accent mt-0.5" />
                <a
                  href="tel:+18882603299"
                  className="text-sm text-secondary-foreground/80 hover:text-accent transition-colors duration-300"
                >
                  +1 (888) 260-3299
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <Icon name="MapPinIcon" size={20} className="text-accent mt-0.5" />
                <span className="text-sm text-secondary-foreground/80">
                  Phoenix, AZ
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-secondary-foreground/20">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-secondary-foreground/60">
              © <span suppressHydrationWarning>{currentYear}</span> Red Letter X. All rights reserved.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="p-2 text-secondary-foreground/60 hover:text-accent hover:bg-secondary-foreground/10 rounded-md transition-all duration-300"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} />
                </a>
              ))}
            </div>

            {/* Legal Links */}
            <div className="flex items-center space-x-4 text-sm">
              <Link
                href="#"
                className="text-secondary-foreground/60 hover:text-accent transition-colors duration-300"
              >
                Privacy Policy
              </Link>
              <span className="text-secondary-foreground/40">|</span>
              <Link
                href="#"
                className="text-secondary-foreground/60 hover:text-accent transition-colors duration-300"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;